import PluginContributions, {
   type Contribution,
} from "*external*/core/utils/PluginContributions";
import legacyFeatureFlagApi from "*external*/core/plugin/featureFlags";
import serviceRegistry from "*external*/core/utils/serviceRegistry";

type SystemFlag = {
   description: string;
   enabled: boolean;
   enabled_for_any: boolean;
   key: string;
   tenant_tags: null | string[];
};

type FlagMeta = {
   id: string;
   label: string;
   deprecated?: {
      message: string;
      setTo?: boolean;
      getValue?: unknown;
   };
};

type FeatureFlag = {
   id: string;
   label: string;
   source: "contribution" | "system";
   deprecated?: string;
   enabled: () => boolean;
};

const storage_key = "cisco.harbor.platform/feature-flags";

// fetched on startup from auth service
export class FeatureFlagStore {
   private _flags: FeatureFlag[] = [];

   constructor(flags: FeatureFlag[] = []) {
      this._flags = flags;
   }

   get flags(): FeatureFlag[] {
      return this._flags;
   }

   private set flags(flags: FeatureFlag[]) {
      this._flags = flags;

      const enabledIds = flags
         .filter((f) => !f.deprecated && f.enabled())
         .map((f) => f.id);

      localStorage.setItem(storage_key, JSON.stringify(enabledIds));
   }

   get = (id: string): FeatureFlag | undefined =>
      this.flags.find((x) => x.id === id);

   add = (flag: FeatureFlag): void => {
      this.flags = this.flags.concat(flag);
   };

   enable = (id: string): void => {
      this.flags = this.flags.map((flag) =>
         flag.id === id ? { ...flag, enabled: () => true } : flag
      );
   };

   disable = (id: string): void => {
      this.flags = this.flags.map((flag) =>
         flag.id === id ? { ...flag, enabled: () => false } : flag
      );
   };
}

export const store = new FeatureFlagStore();

const flagFromContribution = async (
   meta: Contribution<FlagMeta>,
   savedIds: string[]
) => {
   const id = meta.sourcePluginId + "/" + meta.data.id;
   const flag: FeatureFlag = {
      id,
      source: "contribution",
      label: meta.data.label,
      enabled: () => savedIds.includes(id),
   };

   const { deprecated } = meta.data;
   if (deprecated) {
      flag.deprecated = deprecated.message;
      flag.enabled = deprecated.getValue
         ? await meta.loadModule(deprecated.getValue)
         : () => deprecated.setTo ?? false;
   }

   return flag;
};

const flagFromSystem = (
   flag: SystemFlag,
   tenantTags: string[]
): FeatureFlag => ({
   id: "cisco.dna.core.system/" + flag.key,
   label: flag.description,
   source: "system",
   enabled: () => {
      if (!flag.enabled) return false;
      if (flag.enabled_for_any) return true;
      return (flag.tenant_tags || [])
         .map((tag) => tag.toLowerCase())
         .some((tag) => tenantTags.includes(tag));
   },
});

export const initStore = async (store: FeatureFlagStore): Promise<void> => {
   const contribs = [
      ...new PluginContributions("cisco.dna.core").forExtensionPoint<FlagMeta>(
         "featureFlag"
      ),
      ...new PluginContributions(
         "cisco.dna.core.featureFlags"
      ).forExtensionPoint<FlagMeta>("flag"),
      ...new PluginContributions(
         "cisco.harbor.platform"
      ).forExtensionPoint<FlagMeta>("feature-flag"),
   ];

   const savedState = JSON.parse(localStorage.getItem(storage_key) || "[]");

   await Promise.all(
      contribs.map(async (data) => {
         const flag = await flagFromContribution(data, savedState);
         store.add(flag);
      })
   );

   const tenantTags = await serviceRegistry
      .getService("cisco.dna.core.auth", "session")
      .then((x) => x.tenantTags)
      .catch((_) => []);

   (window.platformUI.featureFlags || [])
      .map((x) => flagFromSystem(x, tenantTags))
      .forEach(store.add);
};

export async function startup(): Promise<void> {
   // Provide backwards compatibility
   Object.assign(legacyFeatureFlagApi, {
      isEnabled: (flagId: string) => store.get(flagId)?.enabled() ?? false,
      enable: (id: string) => store.enable(id),
      disable: (id: string) => store.disable(id),
   });

   await initStore(store);
}
